import React, { useRef, useEffect, useState, useContext, useCallback } from 'react';
import tw from 'twin.macro';
import { Icon } from '../icon';
import { AppContext } from '../context';
import { ArrowIcon, TimesIcon, ExternalUrlIcon, PlusIcon, MinusIcon, DumpertLogoIcon } from 'icons';
import { Button } from '../button';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// Constants for better readability and maintenance
const ZOOM_MIN = 50;
const ZOOM_MAX = 800;
const ZOOM_STEP = 0.2;
const INITIAL_ZOOM = 100;
const KEY_CODES = {
  ESC: 27,
  LEFT: 37,
  RIGHT: 39,
};

const wrap = (min, max, v) => {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

// Create a completely separate component to force a full re-render
// This ensures no state from previous renders affects the current image
const ImageSliderContent = ({ imageSlider, setImageSlider, windowSize, selectedImage }) => {
  const { images = [] } = imageSlider;
  const length = images.length || 0;
  const hasMultipleImages = length > 1;
  const imagesList = length > 0 ? images.map((image) => image.variants[0].uri) : [];

  const transformComponentRef = useRef(null);
  const containerRef = useRef(null);

  // Keep track if we've added our own history entry
  const historyEntryAddedRef = useRef(false);
  // Store the original URL when the slider was opened
  const originalUrlRef = useRef('');

  const [page, setPage] = useState(selectedImage);
  const [zoomLevel, setZoomLevel] = useState(INITIAL_ZOOM);

  const imageIndex = wrap(0, length, page);

  // Memoize handlers for better performance
  const resetZoom = useCallback((duration = 0) => {
    if (transformComponentRef.current) {
      transformComponentRef.current.resetTransform(duration);
    }
  }, []);

  const paginate = useCallback(
    (newDirection) => {
      setPage((prevPage) => prevPage + newDirection);
      resetZoom();
    },
    [resetZoom],
  );

  const closeImageSlider = useCallback(() => {
    if (historyEntryAddedRef.current && window.history.state && window.history.state.imageSlider) {
      // If we added a history entry, use the back button to close
      window.history.back();
    } else {
      // Just update the state directly if we didn't add a history entry
      setImageSlider({
        ...imageSlider,
        active: false,
        selected: imageIndex,
      });
    }
  }, [imageSlider, setImageSlider, imageIndex]);

  const onKeyUp = useCallback(
    (e) => {
      if (e.keyCode === KEY_CODES.ESC) {
        closeImageSlider();
      }

      if (hasMultipleImages) {
        if (e.keyCode === KEY_CODES.LEFT) {
          paginate(-1);
        }
        if (e.keyCode === KEY_CODES.RIGHT) {
          paginate(1);
        }
      }
    },
    [closeImageSlider, hasMultipleImages, paginate],
  );

  const handleZoomIn = useCallback(() => {
    if (zoomLevel < ZOOM_MAX && transformComponentRef.current) {
      transformComponentRef.current.zoomIn(ZOOM_STEP, 0);
      const newScale = transformComponentRef.current.instance.transformState.scale;
      setZoomLevel(Math.round(newScale * 100));
    }
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    if (zoomLevel > ZOOM_MIN && transformComponentRef.current) {
      transformComponentRef.current.zoomOut(ZOOM_STEP, 0);
      const newScale = transformComponentRef.current.instance.transformState.scale;
      setZoomLevel(Math.round(newScale * 100));
    }
  }, [zoomLevel]);

  const handleDoubleClick = useCallback(
    ({ zoomIn, resetTransform }) => {
      if (zoomLevel === INITIAL_ZOOM) {
        zoomIn(1, 200);
      } else {
        resetTransform(200);
      }
    },
    [zoomLevel],
  );

  // Handle initialization - runs only once when component mounts
  useEffect(() => {
    // Store the current URL - we might need it later to properly restore navigation
    originalUrlRef.current = window.location.href;

    // Hide the page's scrollbars while the slider is active
    document.body.style.overflow = 'hidden';

    return () => {
      // Restore scroll behavior when component unmounts
      document.body.style.overflow = '';
    };
  }, []);

  // Handle history state and popstate events
  useEffect(() => {
    // Function to handle browser back button
    const handlePopState = (event) => {
      // Set the imageSlider to inactive when user presses back
      setImageSlider({
        ...imageSlider,
        active: false,
        selected: imageIndex,
      });
    };

    // Only alter history if we haven't already done so for this slider activation
    // This prevents multiple history entries when paginating through images
    if (!historyEntryAddedRef.current) {
      // First, replace the current history entry to preserve it
      // This ensures we can return to this exact state after closing the image slider
      const currentState = {
        originalUrl: originalUrlRef.current,
        timestamp: Date.now(),
      };

      window.history.replaceState(currentState, '');

      // Then add a new history entry for the image slider
      // This will become the entry we'll go back from
      window.history.pushState(
        {
          imageSlider: true,
          timestamp: Date.now(),
        },
        '',
      );

      historyEntryAddedRef.current = true;
    }

    // Listen for browser back button
    window.addEventListener('popstate', handlePopState);

    // Ensure transform is reset when the component mounts
    resetZoom();

    return () => {
      // Remove the popstate listener
      window.removeEventListener('popstate', handlePopState);
    };
  }, [imageSlider, setImageSlider, imageIndex, resetZoom]);

  // Handle keyboard navigation
  useEffect(() => {
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [onKeyUp]);

  // Only render if there are images
  if (!length) return null;

  return (
    <div tw='w-screen h-screen sticky top-0 left-0 z-50'>
      <div tw='bg-primary absolute inset-0 max-h-screen z-40 flex flex-col'>
        <div tw='absolute z-50 top-[22px] sm:top-[17px] sm:left-4 lg:top-[17px] left-2'>
          <DumpertLogoIcon color='#66CC22' tw='w-[80px] sm:w-[150px]' />
        </div>

        {/* Header with external link and close button */}
        <div tw='flex justify-end items-center p-4 pr-2 sm:pr-4 h-14'>
          <div tw='flex items-center h-full'>
            {images.length > 0 && imageIndex < images.length && (
              <a
                href={images[imageIndex]?.variants[0]?.uri}
                target='_blank'
                rel='noopener noreferrer'
                title='Opklik voor groot'
                tw='h-full cursor-pointer flex items-center'
                aria-label='Open in een nieuw venster'
              >
                <Button
                  label='Open in een nieuw venster'
                  iconProps={{ Type: ExternalUrlIcon }}
                  hideLabel={['MOBILE'].includes(windowSize.adBreakPoint)}
                />
              </a>
            )}
            <button
              onClick={closeImageSlider}
              tw='cursor-pointer z-50 ml-4 flex items-center h-full'
              data-testid='close-button'
              aria-label='Sluiten'
            >
              <Icon Type={TimesIcon} />
            </button>
          </div>
        </div>

        {/* Image Viewer with TransformWrapper */}
        <TransformWrapper
          initialScale={1.0}
          minScale={0.5}
          maxScale={8}
          centerOnInit={true}
          defaultScale={1.0}
          defaultPositionX={0}
          defaultPositionY={0}
          centerContent={true}
          limitToBounds={true}
          fitToViewport={false}
          animations={false}
          wheel={{ disabled: false }}
          alignmentAnimation={{ disabled: true }}
          panning={{ disabled: false }}
          doubleClick={{ disabled: true }}
          disablePadding={true}
          ref={transformComponentRef}
          onTransformed={(ref) => {
            const scale = ref.instance.transformState.scale;
            setZoomLevel(Math.round(scale * 100));
          }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div tw='w-full h-full relative flex overflow-hidden'>
                {/* Navigation arrows for desktop */}
                {hasMultipleImages && (
                  <>
                    <button
                      tw='hidden w-32 md:flex cursor-pointer justify-center items-center z-10 absolute top-0 left-0 bottom-0'
                      onClick={() => paginate(-1)}
                      data-testid='prev-button'
                      aria-label='Vorige afbeelding'
                    >
                      <Icon
                        Type={ArrowIcon}
                        size='xlarge'
                        tw='text-white -rotate-90 bg-dark bg-opacity-50 rounded-full p-2'
                      />
                    </button>
                    <button
                      tw='w-32 hidden md:flex cursor-pointer justify-center items-center z-10 absolute top-0 right-0 bottom-0'
                      onClick={() => paginate(1)}
                      data-testid='next-button'
                      aria-label='Volgende afbeelding'
                    >
                      <Icon
                        Type={ArrowIcon}
                        size='xlarge'
                        tw='text-white rotate-90 bg-dark bg-opacity-50 rounded-full p-2'
                      />
                    </button>
                  </>
                )}

                {/* Image container */}
                <div tw='w-full flex justify-center items-center relative'>
                  <div
                    tw='h-full w-full relative flex justify-center items-center overflow-hidden'
                    ref={containerRef}
                    onDoubleClick={() => handleDoubleClick({ zoomIn, resetTransform })}
                  >
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        cursor: 'grab',
                      }}
                      contentStyle={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        top:
                          !hasMultipleImages && windowSize.adBreakPoint === 'MOBILE'
                            ? '-28px'
                            : '0',
                        width: '100%',
                        height: '100%',
                        transition: 'none',
                      }}
                    >
                      {imagesList.length > 0 &&
                        imageIndex < imagesList.length &&
                        imagesList[imageIndex] && (
                          <img
                            src={imagesList[imageIndex]}
                            tw='max-h-[100%] max-w-[100%] object-contain block mx-auto transition-none'
                            alt={`Afbeelding ${imageIndex + 1} van ${length}`}
                            loading='eager'
                          />
                        )}
                    </TransformComponent>
                  </div>
                </div>
              </div>

              {/* Zoom controls */}
              <div
                tw='flex fixed left-1/2 transform -translate-x-1/2 z-50
                   bg-dark bg-opacity-50 rounded-md p-2 flex-col justify-center items-center pointer-events-none'
                css={[hasMultipleImages ? tw`bottom-16 md:bottom-2` : tw`bottom-2`]}
              >
                {hasMultipleImages && (
                  <div tw='hidden md:flex text-gray-300 text-sm font-medium select-none mb-2'>
                    <span tw='font-bold w-6 inline-block text-center text-white'>
                      {imageIndex + 1}
                    </span>
                    /<span tw='w-6 inline-block text-center'>{length}</span>
                  </div>
                )}
                <div tw='flex gap-4 justify-center items-center'>
                  <button
                    tw='cursor-pointer bg-brand rounded-full w-8 h-8 flex justify-center items-center pointer-events-auto'
                    css={[
                      zoomLevel <= ZOOM_MIN ? tw`opacity-50 cursor-not-allowed` : tw`opacity-100`,
                    ]}
                    onClick={handleZoomOut}
                    title='Zoom uit'
                    data-testid='zoom-out-button'
                    aria-label='Zoom uit'
                    disabled={zoomLevel <= ZOOM_MIN}
                  >
                    <Icon Type={MinusIcon} size='medium' color='dark' />
                  </button>
                  <div tw='text-white text-sm w-10 text-center font-medium select-none'>
                    {zoomLevel}%
                  </div>
                  <button
                    tw='cursor-pointer bg-brand rounded-full w-8 h-8 flex justify-center items-center pointer-events-auto'
                    css={[
                      zoomLevel >= ZOOM_MAX ? tw`opacity-50 cursor-not-allowed` : tw`opacity-100`,
                    ]}
                    onClick={handleZoomIn}
                    title='Zoom in'
                    data-testid='zoom-in-button'
                    aria-label='Zoom in'
                    disabled={zoomLevel >= ZOOM_MAX}
                  >
                    <Icon Type={PlusIcon} size='medium' color='dark' />
                  </button>
                </div>
              </div>
            </>
          )}
        </TransformWrapper>

        {/* Mobile navigation controls */}
        {hasMultipleImages && (
          <div tw='md:hidden'>
            <div tw='flex justify-between items-center'>
              <button
                tw='w-32 p-4 cursor-pointer flex justify-start items-center z-10'
                onClick={() => paginate(-1)}
                data-testid='prev-button-bottom'
                aria-label='Vorige afbeelding'
              >
                <Icon Type={ArrowIcon} tw='-rotate-90 text-white' />
              </button>

              <div tw='text-gray-300 text-sm font-medium'>
                <span tw='font-bold w-6 inline-block text-center text-white'>{imageIndex + 1}</span>
                /<span tw='w-6 inline-block text-center'>{length}</span>
              </div>

              <button
                tw='w-32 p-4 cursor-pointer flex justify-end items-center z-10'
                onClick={() => paginate(1)}
                data-testid='next-button-bottom'
                aria-label='Volgende afbeelding'
              >
                <Icon Type={ArrowIcon} tw='rotate-90 text-white' />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ImageSlider = () => {
  const {
    imageSlider,
    imageSlider: { active, selected = 0 },
    setImageSlider,
    windowSize,
  } = useContext(AppContext);

  // Using a completely fresh instance for each activation of the image slider
  // by using the selected image as a key ensures that no previous state is reused
  if (!active) return null;

  return (
    <ImageSliderContent
      key={`slider-instance-${selected}`}
      imageSlider={imageSlider}
      setImageSlider={setImageSlider}
      windowSize={windowSize}
      selectedImage={selected}
    />
  );
};
